import { Injectable } from '@angular/core'
import { AbstractControl, FormGroup } from '@angular/forms'
import { Subject } from 'rxjs'
import { FormIdentificator, formSchemaRegistry } from '@onedv-flexgate/shared'

export interface FormRegistrant {
  formIdentificator: FormIdentificator
  form: FormGroup
  submit: () => void
}

@Injectable({ providedIn: 'root' })
export class FormRegistryService {
  private current: FormRegistrant | null = null
  private snapshot: string | null = null
  private readonly registeredSubject = new Subject<FormRegistrant>()

  readonly registered$ = this.registeredSubject.asObservable()

  get currentRegistrant(): FormRegistrant | null {
    return this.current
  }

  register(registrant: FormRegistrant): void {
    this.current = registrant
    this.registeredSubject.next(registrant)
  }

  unregister(): void {
    this.current = null
    this.snapshot = null
  }

  submit(): boolean {
    if (!this.current) return false
    const { form, formIdentificator } = this.current
    const schema = formSchemaRegistry[formIdentificator]
    const result = schema.safeParse(form.getRawValue())
    if (!result.success) {
      form.markAllAsTouched()
      this.markAllAsDirty(form)
      this.updateAllValidity(form)
      return false
    }
    this.current.submit()
    return true
  }

  takeSnapshot(): void {
    const form = this.current?.form
    this.snapshot = form ? JSON.stringify(form.getRawValue()) : null
  }

  hasUnsavedChanges(): boolean {
    const form = this.current?.form
    if (!form || this.snapshot === null) return false
    return JSON.stringify(form.getRawValue()) !== this.snapshot
  }

  private updateAllValidity(control: AbstractControl): void {
    if (control instanceof FormGroup) {
      Object.values(control.controls).forEach((c) => this.updateAllValidity(c))
    }
    control.updateValueAndValidity({ onlySelf: true })
  }

  private markAllAsDirty(control: AbstractControl): void {
    control.markAsDirty({ onlySelf: true })
    if (control instanceof FormGroup) {
      Object.values(control.controls).forEach((c) => this.markAllAsDirty(c))
    }
  }
}
